import { Typography } from '@ag1/ui';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef } from 'react';
import { useStoreValue } from 'state/hooks/useStore';
import { useStoreCurrency } from 'state/hooks/useStoreCurrency';
import { useUnfortunateWayToAppendCurrency } from 'state/hooks/useUnfortunateWayToAppendCurrency';
import { useProductStore } from 'state/store/pdpStore';
import { PriceSelector as PriceSelectorProps, SanityKeyed } from 'types/studio/studio';
import {
  SHOPIFY_AGZ_SUB_BUNDLE_VARIANT_IDS,
  SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID
} from '@portal/config/base';
import { isPouchOrTravelPackV53Variant } from '@portal/shared/utils/shopify/isPouchOrTravelPackV53Variant';
import { CommonSectionProps } from '../../types';
import { getSelectedVariant, getUniqueVariantId } from '../utils';
import BundlePriceInfo from './BundlePriceInfo/BundlePriceInfo';
import { PriceInput } from './PriceInput/PriceInput';
import styles from './PriceSelector.module.scss';
import { useSpecialMemberPricing } from '../useSpecialMemberPricing';
import { useIsLoggedInValue } from '@portal/appLayer/auth/useIsLoggedInValue';
import { useGateValue } from '@statsig/react-bindings';
import { SSGates } from '@portal/state/context/StatsigContext/StatsigFlags';
import { convertToCamelCase } from '@portal/shared/utils/convertToCamelCase';

// Purchase type options
export const PLAN_TYPES = {
  SUBSCRIPTION: 'subscription',
  ONE_TIME: 'one_time'
};

const PriceSelector = (props: SanityKeyed<PriceSelectorProps> & CommonSectionProps) => {
  const {
    _key: key,
    headerText,
    dividerText,
    bundlePriceText,
    hasCadenceSelector,
    cadenceOptions,
    addSuperScript,
    addSuperScriptLis,
    hideStrikeThroughLIS,
    defaultDeliveryCadenceAssurance,
    oldPriceFirstToggle = false
  } = props;
  const store = useStoreValue();
  const router = useRouter();
  const hasInitialized = useRef(false);
  const { symbolCurrency } = useStoreCurrency();
  const { conditionallyAppendUSDtoPrice } = useUnfortunateWayToAppendCurrency();

  const setIsOtpProduct = useProductStore((state) => state.setIsOtpProduct);
  const isOtpProduct = useProductStore((state) => state.isOtpProduct);
  const setSelectedVariantId = useProductStore((state) => state.setSelectedVariantId);
  const setProductCadence = useProductStore((state) => state.setProductCadence);
  const variants = useProductStore((state) => state.variants);
  const selectedVariantId = useProductStore((state) => state.selectedVariantId);
  const rawSelectedVariantId = (selectedVariantId ?? '').split('-')[0];
  const flavor = useProductStore((state) => state.flavor);
  const pdpType = useProductStore((state) => state.pdpType);
  const bundleVariant = useProductStore((state) => state.bundleVariant);
  const { showMemberPrice, isSubscriptionLoading, isOmegaProduct, isAgzProduct } =
    useSpecialMemberPricing(rawSelectedVariantId, store);
  const isLoggedIn = useIsLoggedInValue();
  const isAgzDiscountEnabled = useGateValue(SSGates.agzActiveSubscriptionDiscount);
  const isBundlePdP = pdpType === 'bundleView';
  const isOtpPdP = pdpType === 'otpView';

  useEffect(() => {
    const defaultVariantId = variants ? getUniqueVariantId(variants[0], flavor) : '';

    // Only set default variant once when variants are loaded and we don't have one yet
    if (variants?.length && !selectedVariantId && defaultVariantId) {
      setSelectedVariantId(defaultVariantId);
    }
  }, [variants, selectedVariantId, flavor, setSelectedVariantId]);

  const formattedCadenceOptions = useMemo(() => {
    if (!cadenceOptions || !store) return [];

    return cadenceOptions
      .filter((option) => {
        if (!option.sellingPlans || option.sellingPlans.length === 0) return true;

        // Check if any sellingPlan matches the current store
        return option.sellingPlans.some((plan) => {
          if (!plan.targetStore) return true;
          return plan.targetStore === store;
        });
      })
      .map((option) => {
        const newOption = { ...option };

        // Filter sellingPlans to only include the current store
        if (newOption.sellingPlans && newOption.sellingPlans.length > 0) {
          newOption.sellingPlans = newOption.sellingPlans.filter(
            (plan) => !plan.targetStore || plan.targetStore === store
          );
        }

        return newOption;
      });
  }, [cadenceOptions, store]);

  // Sync state with URL query parameters
  useEffect(() => {
    if (router.isReady) {
      const { plan } = router.query;

      if (plan === PLAN_TYPES.ONE_TIME || isOtpPdP) {
        setIsOtpProduct(true);
      } else if (plan === PLAN_TYPES.SUBSCRIPTION || !hasInitialized.current) {
        // Default to subscription if not specified or on first load
        setIsOtpProduct(false);
      }

      hasInitialized.current = true;
    }
  }, [router.isReady, router.query, isOtpPdP, setIsOtpProduct]);

  useEffect(() => {
    if (isBundlePdP && formattedCadenceOptions?.length > 0) {
      setProductCadence(formattedCadenceOptions[0]?.sellingPlans?.[0]?.sellingPlan || '');
    }
  }, [isBundlePdP, formattedCadenceOptions, setProductCadence]);

  useEffect(() => {
    if (showMemberPrice && (isOmegaProduct || isAgzProduct) && (store === 'qa' || store === 'us')) {
      let memberVariant = null;
      if (
        isOmegaProduct &&
        rawSelectedVariantId !== SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID[store].bundlePrice
      ) {
        memberVariant = variants?.find(
          (variant) =>
            variant?.subscriptionVariantIds?.[0]?.variantId ===
            SHOPIFY_OMEGA_SUB_BUNDLE_VARIANT_ID[store].bundlePrice
        );

        if (memberVariant) {
          setSelectedVariantId(getUniqueVariantId(memberVariant, flavor));
        }
      }

      if (isAgzProduct) {
        memberVariant = null;
        if (isAgzProduct) {
          const flavorKey =
            flavor as keyof (typeof SHOPIFY_AGZ_SUB_BUNDLE_VARIANT_IDS)[typeof store];
          const agzStoreVariants = SHOPIFY_AGZ_SUB_BUNDLE_VARIANT_IDS[store];
          const agzFlavorVariant =
            agzStoreVariants?.[convertToCamelCase(flavorKey) as keyof typeof agzStoreVariants];
          const agzBundlePriceVariantId = agzFlavorVariant?.bundlePrice;

          memberVariant = variants?.find(
            (variant) =>
              Array.isArray(variant?.subscriptionVariantIds) &&
              variant.subscriptionVariantIds.some(
                (subVariant) => subVariant?.variantId === agzBundlePriceVariantId
              )
          );
        }

        if (memberVariant && isAgzDiscountEnabled) {
          setSelectedVariantId(getUniqueVariantId(memberVariant, flavor));
        }
      }
    }
  }, [
    showMemberPrice,
    selectedVariantId,
    setSelectedVariantId,
    isOmegaProduct,
    isAgzProduct,
    store,
    variants,
    rawSelectedVariantId,
    flavor,
    isAgzDiscountEnabled
  ]);

  const selectedVariant = useMemo(
    () => getSelectedVariant(variants, selectedVariantId ?? '', store, flavor),
    [variants, selectedVariantId, flavor, store]
  );

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isOtp = value === 'otp';

    // Update query parameters
    const newQuery: Record<string, string> = {
      ...(router.query as Record<string, string>),
      plan: isOtp ? PLAN_TYPES.ONE_TIME : PLAN_TYPES.SUBSCRIPTION
    };

    // Also update local state for immediate UI update
    setIsOtpProduct(isOtp);

    if (variants && variants.length > 0 && variants[0]) {
      // Only update the quantity parameter in the URL
      newQuery.quantity = String(variants[0].quantity);
    }

    // Update URL without reloading the page
    router.replace(
      {
        pathname: router.pathname,
        query: newQuery
      },
      undefined,
      { shallow: true }
    );
  };

  // Determine if the selected variant is a pouch or pack PDP
  const subVarId = selectedVariant?.subscriptionVariantIds?.[0]?.variantId;
  const isPouchOrPackPdp = !!subVarId && isPouchOrTravelPackV53Variant(subVarId, store);

  // Select the correct OTP price based on login state
  const otpPrice =
    isLoggedIn && isPouchOrPackPdp && selectedVariant?.pdpMemberOtpPrice
      ? selectedVariant?.pdpMemberOtpPrice
      : selectedVariant?.oneTimePrice;

  if ((!selectedVariant && !bundleVariant) || isSubscriptionLoading) {
    return null;
  }

  if (isBundlePdP && bundleVariant) {
    return (
      <BundlePriceInfo id={key} bundlePriceText={bundlePriceText} addSuperScript={addSuperScript} />
    );
  }

  if (isOtpPdP) {
    return (
      <div>
        <div className={clsx(styles.divider, styles.otpDivider)}>
          <div className={styles.line} />
        </div>
        <div className={styles.otpPriceContainer}>
          <span className={styles.otpPriceLabel}>
            {selectedVariant
              ? `${symbolCurrency}${conditionallyAppendUSDtoPrice(selectedVariant.price)}`
              : ''}
          </span>
          <span className={styles.otpPricePerServing}>
            {selectedVariant ? selectedVariant.otpPerServingText : ''}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.priceContainer} id={key}>
      <div className={styles.priceSectionLabel}>
        <Typography
          variant="subtitleMono"
          size="2"
          noPadding
          color="black-new"
          className={styles.priceSectionHeading}>
          {`${headerText}:`}
        </Typography>
      </div>
      <div className={styles.priceSection}>
        {selectedVariant && (
          <PriceInput
            {...selectedVariant}
            hasCadenceSelector={hasCadenceSelector}
            variantId={selectedVariant?.subscriptionVariantIds?.[0]?.variantId}
            onChange={onChange}
            title={selectedVariant?.subscriptionLabel}
            isOneTime={false}
            checked={!isOtpProduct}
            cadenceOptions={formattedCadenceOptions}
            addSuperScript={addSuperScript}
            addSuperScriptLis={addSuperScriptLis}
            hideStrikeThroughLIS={hideStrikeThroughLIS}
            oldPriceFirst={oldPriceFirstToggle}
            titleMembers={selectedVariant?.subscriptionLabelMembers}
            defaultDeliveryCadenceAssurance={defaultDeliveryCadenceAssurance}
          />
        )}
      </div>
      <div className={styles.divider}>
        <div className={styles.line} />
        <div className={styles.orText}>{dividerText}</div>
        <div className={styles.line} />
      </div>
      <div className={styles.priceSection}>
        {selectedVariant && (
          <PriceInput
            {...selectedVariant}
            oneTimePrice={otpPrice || ''}
            variantId={
              selectedVariant?.oneTimeVariantId &&
              selectedVariant.oneTimeVariantId.length > 0 &&
              !isLoggedIn
                ? selectedVariant.oneTimeVariantId?.[0].variantId
                : selectedVariant.subscriptionVariantIds?.[0]?.variantId
            }
            titleMembers={selectedVariant?.subscriptionLabelMembers}
            onChange={onChange}
            isOneTime
            checked={isOtpProduct}
            oldPriceFirst={oldPriceFirstToggle}
            addSuperScriptLis={addSuperScriptLis}
          />
        )}
      </div>
    </div>
  );
};

export default PriceSelector;
